import type { ReactNode } from 'react';

import { cn } from '@/lib/utils';

const DIGITS = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9];

/**
 * Одна цифра, що котиться як у механічному лічильнику.
 *
 * Замість підміни вузла зсуваємо колонку 0–9 на `-value × 10%`. Це дає плавний
 * перехід без розмонтування, а головне — при зменшенні значення цифри котяться
 * ВНИЗ, що для зворотного відліку читається природно.
 *
 * Анімація вимикається за prefers-reduced-motion: рух тут декоративний,
 * а значення однаково доступне текстом.
 */
export function OdometerDigit({ value, className }: { value: number; className?: string }): ReactNode {
	const safe = Math.min(9, Math.max(0, Math.trunc(value)));

	return (
		<span className={cn('block h-[1em] overflow-hidden leading-none', className)} aria-hidden="true">
			<span
				className="block transition-transform duration-500 ease-out motion-reduce:transition-none motion-reduce:duration-0"
				style={{ transform: `translateY(-${safe * 10}%)` }}
			>
				{DIGITS.map((d) => (
					<span key={d} className="block h-[1em] leading-none tabular-nums">
						{d}
					</span>
				))}
			</span>
		</span>
	);
}

/** Число з фіксованою кількістю розрядів, кожен котиться окремо. */
export function OdometerNumber({
	value,
	digits = 2,
	className,
}: {
	value: number;
	digits?: number;
	className?: string;
}): ReactNode {
	const text = String(Math.max(0, Math.trunc(value))).padStart(digits, '0');

	return (
		<span className={cn('inline-flex', className)}>
			{/* Значення для скрінрідера — один раз, звичайним текстом. */}
			<span className="sr-only">{text}</span>
			{text.split('').map((char, i) => (
				<OdometerDigit key={`${text.length}-${i}`} value={Number(char)} />
			))}
		</span>
	);
}
